import React, { useEffect, useRef, useState } from 'react';
import * as BABYLON from '@babylonjs/core';
import { GridMaterial } from '@babylonjs/materials';
import { styled, useStyletron } from 'baseui';
import { Skeleton } from 'baseui/skeleton';
import { ResizeObserver } from 'global';
import type { StyleObject } from 'styletron-react';
import '@babylonjs/loaders';
import Timeline from './timeline';
const Wrapper = styled('div', () => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column'
}));
const loadingOverrides = {
  Root: {
    style: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0
    }
  }
};
type SceneRenderPropsT = {
  antialias?: boolean;
  url: string;
  id: string;
  styles?: StyleObject;
};
export const SceneRender = ({
  antialias = true,
  url,
  id,
  styles = {}
}: SceneRenderPropsT) => {
  const [css] = useStyletron();
  const [scene, setScene] = useState(null);
  const [fps, setFps] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const reactCanvas = useRef(null);

  // set up basic engine and scene
  useEffect(() => {
    const {
      current: canvas
    } = reactCanvas;
    if (!canvas) return;
    const engine = new BABYLON.Engine(canvas, antialias, {
      antialias: true,
      adaptToDeviceRatio: true
    });
    const scene = new BABYLON.Scene(engine);
    if (scene.isReady()) {
      // This creates and positions an arc rotate camera (non-mesh)
      var camera = new BABYLON.ArcRotateCamera('Camera', BABYLON.Tools.ToRadians(90),
      // Alpha (rotation around Y-axis) - Horizontal rotation (look straight)
      BABYLON.Tools.ToRadians(70),
      // Beta (rotation around X-axis) -  Vertical rotation (look straight)
      7,
      // Radius (distance from the center)
      new BABYLON.Vector3(0, 0, 0),
      // Target position (center)
      scene);
      camera.attachControl(canvas, false);

      // This creates a light, aiming 0,1,0 - to the sky (non-mesh)
      var light = new BABYLON.HemisphericLight('light', new BABYLON.Vector3(0, 1, 0), scene);

      // Default intensity is 1. Let's dim the light a small amount
      light.intensity = 0.7;
      var groundMaterial = new GridMaterial('groundMaterial', scene);
      groundMaterial.majorUnitFrequency = 5;
      groundMaterial.minorUnitVisibility = 0.45;
      groundMaterial.gridRatio = 2;
      groundMaterial.backFaceCulling = false;
      groundMaterial.mainColor = new BABYLON.Color3(1, 1, 1);
      groundMaterial.lineColor = new BABYLON.Color3(1.0, 1.0, 1.0);
      groundMaterial.opacity = 0.5;
      var ground = BABYLON.MeshBuilder.CreateGround('ground', {
        width: 20,
        height: 20
      }, scene);
      ground.material = groundMaterial;
      BABYLON.SceneLoader.ImportMesh('', '', url, scene, () => onMeshLoad(scene), undefined, undefined, '.glb');
      setFps(engine.getFps());
    }
    engine.runRenderLoop(() => {
      scene.render();
    });
    const resizeObserver = new (ResizeObserver as typeof global.ResizeObserver)((_entries, _observer) => {
      engine.resize();
    });
    resizeObserver.observe(canvas);
    return () => {
      scene.getEngine().dispose();
      resizeObserver.disconnect();
    };
  }, [antialias, url]);
  const onMeshLoad = scene => {
    setIsLoaded(true);
    setScene(scene);
  };
  return <Wrapper className={css({
    ...styles
  })}>
      <canvas style={{
      width: '100%',
      height: '100%'
    }} ref={reactCanvas} id={id} />
      {!isLoaded && <Skeleton animation width="100%" height="100%" overrides={loadingOverrides} />}
      {scene && isLoaded && <Timeline scene={scene} fps={fps} />}
    </Wrapper>;
};